import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { ArrowLeft, CalendarPlus, Copy, Download, ImagePlus, Images, Loader2, LogOut, Pencil, Plus, RefreshCw, Sparkles, Trash2, Upload, Wand2, X } from "lucide-react";
import Seo from "@/components/Seo";
import {
  addEventImage,
  adminLogin,
  assistArtwork,
  createEvent,
  deleteArtwork,
  deleteEvent,
  deletePortfolioPdf,
  downloadCarouselZip,
  downloadCollectionZip,
  fetchArtworks,
  fetchEnquiries,
  fetchEvents,
  fetchSettings,
  generateCarousel,
  generateCollectionCarousel,
  regenerateArtwork,
  regenerateEvent,
  removeEventImage,
  replaceArtworkImage,
  updateArtwork,
  updateEvent,
  updateSettings,
  uploadArtwork,
  uploadPortfolioPdf,
  uploadSiteImage,
} from "@/lib/api";
import { ARTIST, THEMES, assetUrl, formatApiError, siteImg } from "@/lib/constants";
import { Bindi } from "@/components/motifs";
import { ImageEditor } from "@/components/ImageEditor";

const input = "input-field";
const label = "eyebrow mb-2 block text-muted-foreground";
const EVENT_TYPES = ["Exhibition", "Recognition", "Award", "Workshop", "Other"];
const btn =
  "flex items-center gap-2 rounded-full border border-border px-4 py-2 text-[11px] font-semibold transition-colors hover:border-accent hover:text-accent disabled:opacity-50";

const toForm = (a) => ({
  title: a.title || "",
  intro: a.intro || "",
  story: (a.story || []).join("\n\n"),
  visual_language: (a.visual_language || []).join(", "),
  motifs: (a.motifs || []).join(", "),
  themes: a.themes || [],
  medium: a.medium || "",
  year: a.year || "",
  dimensions: a.dimensions || "",
  cultural_context: a.cultural_context || "",
  featured: !!a.featured,
  order: a.order ?? 0,
});

const seedToForm = (fields) => ({
  title: fields.title ?? "",
  intro: fields.intro ?? "",
  story: Array.isArray(fields.story) ? fields.story.join("\n\n") : fields.story ?? "",
  visual_language: (fields.visual_language || []).join(", "),
  motifs: (fields.motifs || []).join(", "),
  themes: fields.themes || [],
  medium: fields.medium ?? "",
  year: fields.year ?? "",
  dimensions: fields.dimensions ?? "",
  cultural_context: fields.cultural_context ?? "",
});

const EditForm = ({ art, seed, onDone }) => {
  const [form, setForm] = useState(() => (seed ? { ...toForm(art), ...seedToForm(seed), featured: !!art.featured, order: art.order ?? 0 } : toForm(art)));
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggleTheme = (t) =>
    setForm((f) => ({ ...f, themes: f.themes.includes(t) ? f.themes.filter((x) => x !== t) : [...f.themes, t] }));

  const save = async () => {
    setSaving(true);
    try {
      const updated = await updateArtwork(art.id, {
        title: form.title,
        intro: form.intro,
        story: form.story.split(/\n\s*\n/).map((s) => s.trim()).filter(Boolean),
        visual_language: form.visual_language.split(",").map((s) => s.trim()).filter(Boolean),
        motifs: form.motifs.split(",").map((s) => s.trim()).filter(Boolean),
        themes: form.themes,
        medium: form.medium,
        year: form.year,
        dimensions: form.dimensions,
        cultural_context: form.cultural_context,
        featured: form.featured,
        order: Number(form.order) || 0,
      });
      toast.success("Artwork updated");
      onDone(updated);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-4 grid gap-4 border-t border-border pt-6 sm:grid-cols-2" data-testid={`admin-edit-form-${art.id}`}>
      {seed && (
        <p className="flex items-center gap-2 rounded-sm border border-accent/40 bg-accent/5 px-4 py-2 text-[12px] text-accent sm:col-span-2">
          <Wand2 size={13} /> Fields filled from your notes — review, adjust, then save.
        </p>
      )}
      <div>
        <span className={label}>Title</span>
        <input className={input} value={form.title} onChange={set("title")} />
      </div>
      <div className="flex items-end gap-6 pb-1">
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={form.featured} onChange={(e) => setForm((f) => ({ ...f, featured: e.target.checked }))} />
          Featured in hero
        </label>
        <label className="flex items-center gap-2 text-sm">
          Order
          <input type="number" className={`${input} w-20`} value={form.order} onChange={set("order")} />
        </label>
      </div>
      <div className="sm:col-span-2">
        <span className={label}>Poetic introduction</span>
        <textarea rows={2} className={`${input} resize-none`} value={form.intro} onChange={set("intro")} />
      </div>
      <div className="sm:col-span-2">
        <span className={label}>The Story (blank line between paragraphs)</span>
        <textarea rows={8} className={`${input} resize-y`} value={form.story} onChange={set("story")} />
      </div>
      <div>
        <span className={label}>Visual language (comma separated)</span>
        <input className={input} value={form.visual_language} onChange={set("visual_language")} />
      </div>
      <div>
        <span className={label}>Mithila motifs (comma separated)</span>
        <input className={input} value={form.motifs} onChange={set("motifs")} />
      </div>
      <div className="sm:col-span-2">
        <span className={label}>Themes</span>
        <div className="flex flex-wrap gap-2">
          {THEMES.map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => toggleTheme(t)}
              className={`rounded-full border px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.14em] transition-colors ${
                form.themes.includes(t) ? "border-accent bg-accent text-accent-foreground" : "border-border text-muted-foreground"
              }`}
            >
              {t}
            </button>
          ))}
        </div>
      </div>
      <div>
        <span className={label}>Medium (optional)</span>
        <input className={input} value={form.medium} onChange={set("medium")} placeholder="e.g. Acrylic & ink on handmade paper" />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <span className={label}>Year</span>
          <input className={input} value={form.year} onChange={set("year")} />
        </div>
        <div>
          <span className={label}>Dimensions</span>
          <input className={input} value={form.dimensions} onChange={set("dimensions")} />
        </div>
      </div>
      <div className="sm:col-span-2">
        <span className={label}>Cultural context (optional)</span>
        <textarea rows={3} className={`${input} resize-y`} value={form.cultural_context} onChange={set("cultural_context")} />
      </div>
      <div className="flex gap-3 sm:col-span-2">
        <button onClick={save} disabled={saving} data-testid={`admin-save-${art.id}`} className="rounded-full bg-foreground px-6 py-2.5 text-[12px] font-semibold text-background transition-colors hover:bg-accent hover:text-accent-foreground disabled:opacity-50">
          {saving ? "Saving…" : "Save changes"}
        </button>
        <button onClick={() => onDone(null)} className="rounded-full border border-border px-6 py-2.5 text-[12px] font-semibold">
          Cancel
        </button>
      </div>
    </div>
  );
};

const AssistPanel = ({ art, onFilled }) => {
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const run = async () => {
    if (!notes.trim()) return;
    setBusy(true);
    try {
      const { fields } = await assistArtwork(art.id, notes);
      onFilled(fields);
      toast.success("Fields filled from your notes — review below");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "AI assist failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-4 border-t border-border pt-6" data-testid={`assist-panel-${art.id}`}>
      <span className={label}>Write in your own words — AI fills every field</span>
      <textarea
        rows={4}
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        placeholder="e.g. This is Radha and Krishna under a kadamba tree, painted in 2024, acrylic and nib pen on handmade paper, 12 by 16 inches. The story should mention the peacocks and the Yamuna…"
        className={`${input} resize-y`}
        data-testid={`assist-notes-${art.id}`}
      />
      <button onClick={run} disabled={busy || !notes.trim()} data-testid={`assist-run-${art.id}`} className={`${btn} mt-4`}>
        {busy ? <Loader2 size={13} className="animate-spin" /> : <Wand2 size={13} />}
        {busy ? "Thinking…" : "Fill fields with AI"}
      </button>
    </div>
  );
};

const MetadataCard = ({ meta, central }) => {
  if (!meta) return null;
  const Row = ({ k, v }) =>
    v && (Array.isArray(v) ? v.length : true) ? (
      <div className="grid gap-1 sm:grid-cols-[150px_1fr]">
        <span className="font-mono text-[9px] uppercase tracking-[0.16em] text-muted-foreground">{k}</span>
        <span className="text-[13px] leading-relaxed">{Array.isArray(v) ? v.join(" · ") : v}</span>
      </div>
    ) : null;
  return (
    <div className="mt-5 space-y-3 border-t border-border pt-5" data-testid="carousel-metadata">
      {central && <Row k="Central idea" v={central} />}
      <Row k="Short description" v={meta.short_description} />
      <Row k="Central theme" v={meta.central_theme} />
      <Row k="Story / concept" v={meta.story_concept} />
      <Row k="Subject / motifs" v={meta.subject_motifs} />
      <Row k="Mood" v={meta.mood} />
      {!!(meta.colour_palette || []).length && (
        <div className="grid items-center gap-1 sm:grid-cols-[150px_1fr]">
          <span className="font-mono text-[9px] uppercase tracking-[0.16em] text-muted-foreground">Colour palette</span>
          <span className="flex flex-wrap items-center gap-2">
            {meta.colour_palette.map((hex) => (
              <span key={hex} className="flex items-center gap-1.5">
                <span className="h-5 w-5 rounded-full border border-border" style={{ backgroundColor: hex }} />
                <span className="font-mono text-[10px] text-muted-foreground">{hex}</span>
              </span>
            ))}
          </span>
        </div>
      )}
      <Row k="Cultural context" v={meta.cultural_context} />
      <Row k="Keywords" v={meta.keywords} />
      <Row k="Hashtags" v={(meta.hashtags || []).join(" ")} />
    </div>
  );
};

const CarouselPanel = ({ art, existing }) => {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(existing || null);

  const run = async () => {
    setBusy(true);
    try {
      const payload = await generateCarousel(art.id);
      setResult(payload);
      toast.success("Carousel ready — download or copy the caption");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Carousel failed"));
    } finally {
      setBusy(false);
    }
  };

  const copyCaption = async () => {
    try {
      await navigator.clipboard.writeText(result.caption);
      toast.success("Caption copied");
    } catch {
      toast.error("Could not copy");
    }
  };

  return (
    <div className="mt-4 border-t border-border pt-6" data-testid={`carousel-panel-${art.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <span className={label}>Instagram carousel — 1080 × 1350 PNG slides, artwork never altered</span>
        <button onClick={run} disabled={busy} data-testid={`carousel-run-${art.id}`} className={btn}>
          {busy ? <Loader2 size={13} className="animate-spin" /> : <Images size={13} />}
          {busy ? "Composing slides…" : result ? "Regenerate carousel" : "Generate carousel"}
        </button>
      </div>
      {result && (
        <div className="mt-5">
          <div className="flex flex-wrap gap-3">
            {result.slides.map((s, i) => (
              <a key={s} href={assetUrl(s)} target="_blank" rel="noopener noreferrer" data-testid={`carousel-slide-${art.id}-${i}`}>
                <img src={assetUrl(s)} alt={`Slide ${i + 1}`} className="h-36 w-auto border border-border transition-colors hover:border-accent" />
              </a>
            ))}
          </div>
          <MetadataCard meta={result.metadata} central={result.central_idea} />
          <div className="mt-5">
            <span className={label}>Caption</span>
            <textarea readOnly rows={6} value={result.caption} className={`${input} resize-y`} data-testid={`carousel-caption-${art.id}`} />
            <div className="mt-3 flex gap-3">
              <button onClick={copyCaption} className={btn} data-testid={`carousel-copy-${art.id}`}>
                <Copy size={13} /> Copy caption
              </button>
              <button onClick={() => downloadCarouselZip(art.id)} className={btn} data-testid={`carousel-download-${art.id}`}>
                <Download size={13} /> Download all slides (ZIP)
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const toEventForm = (ev) => ({
  title: ev.title || "",
  event_type: ev.event_type || "Exhibition",
  status: ev.status || "past",
  date_text: ev.date_text || "",
  venue: ev.venue || "",
  city: ev.city || "",
  organizer: ev.organizer || "",
  description: ev.description || "",
  order: ev.order ?? 0,
});

const EventForm = ({ event, onDone }) => {
  const [form, setForm] = useState(() => toEventForm(event));
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    setSaving(true);
    try {
      const updated = await updateEvent(event.id, { ...form, order: Number(form.order) || 0 });
      toast.success("Event saved");
      onDone(updated);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-4 grid gap-4 border-t border-border pt-6 sm:grid-cols-2" data-testid={`event-edit-form-${event.id}`}>
      <div className="sm:col-span-2">
        <span className={label}>Title</span>
        <input className={input} value={form.title} onChange={set("title")} data-testid="event-form-title" />
      </div>
      <div>
        <span className={label}>Type</span>
        <select className={`${input} bg-background`} value={form.event_type} onChange={set("event_type")} data-testid="event-form-type">
          {EVENT_TYPES.map((t) => (
            <option key={t} value={t}>{t}</option>
          ))}
        </select>
      </div>
      <div>
        <span className={label}>When</span>
        <select className={`${input} bg-background`} value={form.status} onChange={set("status")} data-testid="event-form-status">
          <option value="upcoming">Upcoming</option>
          <option value="past">Past</option>
        </select>
      </div>
      <div>
        <span className={label}>Date / year (as it should appear)</span>
        <input className={input} value={form.date_text} onChange={set("date_text")} placeholder="e.g. March 2026" />
      </div>
      <div>
        <span className={label}>Organiser</span>
        <input className={input} value={form.organizer} onChange={set("organizer")} />
      </div>
      <div>
        <span className={label}>Venue</span>
        <input className={input} value={form.venue} onChange={set("venue")} />
      </div>
      <div>
        <span className={label}>City</span>
        <input className={input} value={form.city} onChange={set("city")} />
      </div>
      <div className="sm:col-span-2">
        <span className={label}>Description</span>
        <textarea rows={4} className={`${input} resize-y`} value={form.description} onChange={set("description")} />
      </div>
      <div className="flex items-center gap-6 sm:col-span-2">
        <label className="flex items-center gap-2 text-sm">
          Order
          <input type="number" className={`${input} w-20`} value={form.order} onChange={set("order")} />
        </label>
        <div className="flex gap-3">
          <button onClick={save} disabled={saving} data-testid={`event-save-${event.id}`} className="rounded-full bg-foreground px-6 py-2.5 text-[12px] font-semibold text-background transition-colors hover:bg-accent hover:text-accent-foreground disabled:opacity-50">
            {saving ? "Saving…" : "Save event"}
          </button>
          <button onClick={() => onDone(null)} className="rounded-full border border-border px-6 py-2.5 text-[12px] font-semibold">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

const EventImages = ({ ev, onChanged }) => {
  const [busy, setBusy] = useState(false);

  const add = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const updated = await addEventImage(ev.id, file);
      onChanged(updated);
      toast.success("Picture added");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Could not add picture"));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (path) => {
    setBusy(true);
    try {
      const updated = await removeEventImage(ev.id, path);
      onChanged(updated);
      toast.success("Picture removed");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Could not remove picture"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-4 border-t border-border pt-6" data-testid={`event-images-${ev.id}`}>
      <span className={label}>Pictures — the invite stays first; add more from the event</span>
      <div className="flex flex-wrap items-start gap-4">
        {ev.image && (
          <figure className="relative">
            <img src={assetUrl(ev.image)} alt="Event invite" className="h-24 w-auto border border-accent/50 object-contain" />
            <figcaption className="mt-1 font-mono text-[9px] uppercase tracking-[0.18em] text-accent">Invite</figcaption>
          </figure>
        )}
        {(ev.images || []).map((p) => (
          <figure key={p} className="relative">
            <img src={assetUrl(p)} alt="Event picture" className="h-24 w-auto border border-border object-contain" />
            <button
              onClick={() => remove(p)}
              disabled={busy}
              aria-label="Remove picture"
              className="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full border border-border bg-background transition-colors hover:border-destructive hover:text-destructive"
            >
              <X size={11} />
            </button>
          </figure>
        ))}
        <label className={`flex h-24 w-24 cursor-pointer flex-col items-center justify-center gap-1 border border-dashed border-input transition-colors hover:border-accent ${busy ? "opacity-50" : ""}`}>
          {busy ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />}
          <span className="font-mono text-[9px] uppercase tracking-[0.14em] text-muted-foreground">Add</span>
          <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" data-testid={`event-add-image-${ev.id}`} onChange={(e) => { add(e.target.files?.[0]); e.target.value = ""; }} />
        </label>
      </div>
    </div>
  );
};

const SiteImagePicker = ({ kind, title, note, current }) => {
  const [busy, setBusy] = useState(false);
  const [stamp, setStamp] = useState(Date.now());

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const r = await uploadSiteImage(kind, f);
      localStorage.setItem("pk_site_v", String(r.version));
      setStamp(Date.now());
      toast.success(`${title} updated — live on the site now`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Upload failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border border-border bg-background p-5" data-testid={`site-image-${kind}`}>
      <p className="font-serif text-lg font-medium">{title}</p>
      <p className="mt-1 text-[12px] text-muted-foreground">{note}</p>
      <div className="mt-4 flex items-center gap-5">
        <img src={`${assetUrl(`/api/uploads/${current}`)}?v=${stamp}`} alt={title} className="h-20 w-auto border border-border object-contain" />
        <label className={`${btn} cursor-pointer ${busy ? "opacity-50" : ""}`}>
          {busy ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
          Replace image
          <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" data-testid={`site-image-input-${kind}`} onChange={upload} />
        </label>
      </div>
    </div>
  );
};

const PortfolioPdfPanel = ({ custom, onChanged }) => {
  const [busy, setBusy] = useState(false);

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      await uploadPortfolioPdf(f);
      onChanged(true);
      toast.success("Custom portfolio PDF is live — the download buttons now serve your file");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Upload failed"));
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };

  const revert = async () => {
    setBusy(true);
    try {
      await deletePortfolioPdf();
      onChanged(false);
      toast.success("Back to the AI-generated portfolio");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Could not switch back"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border border-border bg-background p-5" data-testid="portfolio-pdf-panel">
      <p className="font-serif text-lg font-medium">Portfolio PDF</p>
      <p className="mt-1 text-[12px] text-muted-foreground">
        Every download button on the site serves the AI-generated portfolio by default. Upload your own PDF here
        to replace it — switch back anytime.
      </p>
      <div className="mt-4 flex flex-wrap items-center gap-4">
        <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-muted-foreground" data-testid="portfolio-pdf-status">
          Now serving: {custom ? <span className="text-accent">Your uploaded PDF</span> : "AI-generated portfolio"}
        </p>
        <label className={`${btn} cursor-pointer ${busy ? "opacity-50" : ""}`}>
          {busy ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
          {custom ? "Replace PDF" : "Upload PDF"}
          <input type="file" accept="application/pdf" className="hidden" data-testid="portfolio-pdf-input" onChange={upload} disabled={busy} />
        </label>
        {custom && (
          <button onClick={revert} disabled={busy} className={btn} data-testid="portfolio-pdf-revert">
            <RefreshCw size={13} /> Back to AI-generated
          </button>
        )}
      </div>
    </div>
  );
};

const CollectionTab = ({ artworks }) => {
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const toggle = (id) =>
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : s.length < 5 ? [...s, id] : s));

  const run = async () => {
    setBusy(true);
    try {
      const payload = await generateCollectionCarousel(selected);
      setResult(payload);
      toast.success("Collection carousel ready — every painting has its own setting");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Carousel failed"));
    } finally {
      setBusy(false);
    }
  };

  const copyCaption = async () => {
    try {
      await navigator.clipboard.writeText(result.caption);
      toast.success("Caption copied");
    } catch {
      toast.error("Could not copy");
    }
  };

  return (
    <div className="max-w-3xl space-y-6" data-testid="collection-tab">
      <div className="border border-border bg-card p-8">
        <h2 className="font-serif text-2xl font-medium tracking-tight">Collection carousel</h2>
        <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
          Choose 2–5 paintings. Each is presented in its own setting — gallery wall, easel, interior,
          studio table, editorial plate — sequenced as one carousel with a single caption in your voice.
          The paintings themselves are never altered.
        </p>
        <div className="mt-6 grid gap-3 sm:grid-cols-2">
          {artworks.map((a) => {
            const on = selected.includes(a.id);
            return (
              <button
                key={a.id}
                onClick={() => toggle(a.id)}
                data-testid={`collection-pick-${a.id}`}
                className={`flex items-center gap-3 border p-3 text-left transition-colors ${on ? "border-accent bg-accent/5" : "border-border hover:border-accent/50"}`}
              >
                <img src={assetUrl(a.image)} alt={a.title} className="h-14 w-auto border border-border object-contain" />
                <span className="min-w-0 flex-1 truncate font-serif text-[15px]">{a.title}</span>
                <span className={`font-mono text-[10px] ${on ? "text-accent" : "text-muted-foreground"}`}>
                  {on ? `#${selected.indexOf(a.id) + 1}` : ""}
                </span>
              </button>
            );
          })}
        </div>
        <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.16em] text-muted-foreground" data-testid="collection-count">
          {selected.length} of 5 selected
        </p>
        <button onClick={run} disabled={busy || selected.length < 2} data-testid="collection-generate-button" className="mt-5 flex items-center gap-2 rounded-full bg-foreground px-6 py-3 text-[12px] font-semibold text-background transition-colors hover:bg-accent hover:text-accent-foreground disabled:opacity-50">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Images size={14} />}
          {busy ? "Composing the collection…" : "Generate collection carousel"}
        </button>
      </div>

      {result && (
        <div className="border border-border bg-card p-8" data-testid="collection-result">
          <div className="flex flex-wrap gap-3">
            {result.slides.map((s, i) => (
              <a key={s} href={assetUrl(s)} target="_blank" rel="noopener noreferrer" data-testid={`collection-slide-${i}`}>
                <img src={assetUrl(s)} alt={`Slide ${i + 1}`} className="h-40 w-auto border border-border transition-colors hover:border-accent" />
              </a>
            ))}
          </div>
          <div className="mt-6">
            <span className={label}>Caption</span>
            <textarea readOnly rows={7} value={result.caption} className={`${input} resize-y`} data-testid="collection-caption" />
            <div className="mt-3 flex flex-wrap gap-3">
              <button onClick={copyCaption} className={btn} data-testid="collection-copy-caption">
                <Copy size={13} /> Copy caption
              </button>
              <button onClick={() => downloadCollectionZip(result.id)} className={btn} data-testid="collection-download-zip">
                <Download size={13} /> Download slides + caption + metadata (ZIP)
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const SiteTab = () => {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings().then(setSettings).catch(() => {});
  }, []);

  if (!settings) return <p className="py-10 font-serif text-lg italic text-muted-foreground">Loading site settings…</p>;

  const save = async () => {
    setSaving(true);
    try {
      const updated = await updateSettings({
        hero_statement: settings.hero_statement,
        artist_quote: settings.artist_quote,
      });
      setSettings(updated);
      localStorage.setItem("pk_site_v", String(updated.version));
      toast.success("Site text updated");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Save failed"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-2xl space-y-6">
      <div className="border border-border bg-card p-8">
        <h2 className="font-serif text-2xl font-medium tracking-tight">Site text</h2>
        <p className="mt-2 text-sm text-muted-foreground">The two sentences visitors read first and remember.</p>
        <div className="mt-6 space-y-5">
          <div>
            <span className={label}>Hero statement (homepage, under your name)</span>
            <textarea rows={2} className={`${input} resize-none`} value={settings.hero_statement} onChange={(e) => setSettings((s) => ({ ...s, hero_statement: e.target.value }))} data-testid="site-statement-input" />
          </div>
          <div>
            <span className={label}>Artist quote (end of The Artist chapter)</span>
            <textarea rows={3} className={`${input} resize-y`} value={settings.artist_quote} onChange={(e) => setSettings((s) => ({ ...s, artist_quote: e.target.value }))} data-testid="site-quote-input" />
          </div>
          <button onClick={save} disabled={saving} data-testid="site-save-button" className="rounded-full bg-foreground px-6 py-2.5 text-[12px] font-semibold text-background transition-colors hover:bg-accent hover:text-accent-foreground disabled:opacity-50">
            {saving ? "Saving…" : "Save text"}
          </button>
        </div>
      </div>
      <SiteImagePicker kind="hero" title="Hero portrait (homepage)" note="The large framed image beside your name on the homepage." current="artist-hero.jpg" />
      <SiteImagePicker kind="portrait" title="Artist portrait (The Artist chapter)" note="The photograph in the biography chapter and PDF." current="portrait.jpg" />
      <SiteImagePicker kind="logo" title="Logo" note="Header, footer, PDFs and carousel slides." current="logo.png" />
      <PortfolioPdfPanel custom={!!settings.custom_portfolio} onChanged={(v) => setSettings((s) => ({ ...s, custom_portfolio: v }))} />
    </div>
  );
};

const Admin = () => {
  const [token, setToken] = useState(() => localStorage.getItem("pk_admin_token"));
  const [creds, setCreds] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [sessionNotice, setSessionNotice] = useState(false);
  const [tab, setTab] = useState("artworks");
  const [artworks, setArtworks] = useState([]);
  const [events, setEvents] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [editing, setEditing] = useState(null);
  const [editSeed, setEditSeed] = useState(null);
  const [assistFor, setAssistFor] = useState(null);
  const [carouselFor, setCarouselFor] = useState(null);
  const [editImageArt, setEditImageArt] = useState(null);
  const [imageBusy, setImageBusy] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [file, setFile] = useState(null);
  const [prepared, setPrepared] = useState(null);
  const [editingImage, setEditingImage] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [eventFile, setEventFile] = useState(null);
  const [eventBusy, setEventBusy] = useState(false);

  const load = () => {
    fetchArtworks().then(setArtworks).catch(() => {});
    fetchEvents().then(setEvents).catch(() => {});
    fetchEnquiries().then(setEnquiries).catch(() => {});
  };

  useEffect(() => {
    if (token) load();
  }, [token]);

  useEffect(() => {
    const onExpired = () => {
      setToken(null);
      setSessionNotice(true);
    };
    window.addEventListener("pk-session-expired", onExpired);
    return () => window.removeEventListener("pk-session-expired", onExpired);
  }, []);

  const login = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const data = await adminLogin(creds);
      localStorage.setItem("pk_admin_token", data.access_token);
      setSessionNotice(false);
      setToken(data.access_token);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail, "Login failed"));
    }
  };

  const logout = () => {
    localStorage.removeItem("pk_admin_token");
    setToken(null);
  };

  const onUpload = async () => {
    const chosen = prepared || file;
    if (!chosen) return;
    setUploading(true);
    try {
      const created = await uploadArtwork(chosen);
      toast.success(`"${created.title}" added with an AI-written catalogue note`);
      setFile(null);
      setPrepared(null);
      setEditingImage(false);
      load();
      setTab("artworks");
      setEditing(created.id);
      setEditSeed(null);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Upload failed"));
    } finally {
      setUploading(false);
    }
  };

  const onReplaceImage = async (editedFile) => {
    if (!editImageArt) return;
    setImageBusy(true);
    try {
      const updated = await replaceArtworkImage(editImageArt.id, editedFile);
      setArtworks((list) => list.map((x) => (x.id === editImageArt.id ? updated : x)));
      setEditImageArt(null);
      toast.success("Painting image updated — the story stays as it was");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Image update failed"));
    } finally {
      setImageBusy(false);
    }
  };

  const onRegenerate = async (id) => {
    setBusyId(id);
    try {
      const updated = await regenerateArtwork(id);
      setArtworks((list) => list.map((a) => (a.id === id ? updated : a)));
      toast.success("Story regenerated — review before publishing");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Regeneration failed"));
    } finally {
      setBusyId(null);
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Remove this artwork from the gallery?")) return;
    await deleteArtwork(id);
    setArtworks((list) => list.filter((a) => a.id !== id));
    toast.success("Artwork removed");
  };

  const onAddEvent = async (withImage) => {
    if (withImage && !eventFile) return;
    setEventBusy(true);
    try {
      const created = await createEvent(withImage ? eventFile : null);
      toast.success(withImage ? "Details read from the picture — review and save" : "Blank event created — fill in the details");
      setEventFile(null);
      load();
      setEditingEvent(created.id);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Could not add event"));
    } finally {
      setEventBusy(false);
    }
  };

  const onRegenerateEvent = async (id) => {
    setBusyId(id);
    try {
      const updated = await regenerateEvent(id);
      setEvents((list) => list.map((e) => (e.id === id ? updated : e)));
      toast.success("Details re-read from the picture");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Regeneration failed"));
    } finally {
      setBusyId(null);
    }
  };

  const onDeleteEvent = async (id) => {
    if (!window.confirm("Remove this event?")) return;
    await deleteEvent(id);
    setEvents((list) => list.filter((e) => e.id !== id));
    toast.success("Event removed");
  };

  const onEventChanged = (updated) =>
    setEvents((list) => list.map((x) => (x.id === updated.id ? updated : x)));

  if (!token) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-5" data-testid="admin-login-page">
        <Seo title={`Studio — ${ARTIST.name}`} siteName={ARTIST.name} robots="noindex, nofollow" />
        <form onSubmit={login} className="w-full max-w-sm border border-border bg-card p-8">
          <div className="mb-8 flex flex-col items-center gap-4 text-center">
            <img src={siteImg("/assets/logo.png")} alt="" className="h-20 w-20 rounded-full object-cover ring-1 ring-foreground/20" />
            <div>
              <h1 className="font-serif text-2xl font-semibold tracking-tight">The Studio</h1>
              <p className="eyebrow mt-1 text-muted-foreground">Artist access only</p>
            </div>
          </div>
          <input type="email" required placeholder="Email" value={creds.email} onChange={(e) => setCreds((c) => ({ ...c, email: e.target.value }))} data-testid="admin-email-input" className={`${input} mb-4`} />
          <input type="password" required placeholder="Password" value={creds.password} onChange={(e) => setCreds((c) => ({ ...c, password: e.target.value }))} data-testid="admin-password-input" className={`${input} mb-4`} />
          {sessionNotice && (
            <p className="mb-4 rounded-sm border border-accent/40 bg-accent/5 px-4 py-2 text-[12px] text-accent" data-testid="admin-session-expired-notice">
              Your session expired — please sign in again.
            </p>
          )}
          {error && <p className="mb-4 text-sm text-accent" data-testid="admin-login-error">{error}</p>}
          <button type="submit" data-testid="admin-login-button" className="w-full rounded-full bg-foreground py-3 text-[13px] font-semibold text-background transition-colors hover:bg-accent hover:text-accent-foreground">
            Enter the studio
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="admin-panel">
      <Seo title={`Studio — ${ARTIST.name}`} siteName={ARTIST.name} robots="noindex, nofollow" />
      <header className="border-b border-border">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-4">
          <div className="flex items-center gap-3">
            <img src={siteImg("/assets/logo.png")} alt="" className="h-10 w-10 rounded-full object-cover ring-1 ring-foreground/20" />
            <div className="leading-tight">
              <p className="font-serif text-lg font-semibold">The Studio</p>
              <p className="eyebrow text-[9px] text-muted-foreground">{ARTIST.name}</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/" data-testid="admin-back-to-site" className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.18em] text-muted-foreground transition-colors hover:text-accent">
              <ArrowLeft size={14} /> View site
            </Link>
            <button onClick={logout} data-testid="admin-logout-button" className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.18em] text-muted-foreground transition-colors hover:text-accent">
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-6xl px-5 py-8">
        <nav className="mb-10 flex flex-wrap gap-2">
          {[
            ["artworks", `Artworks (${artworks.length})`],
            ["add", "Add artwork"],
            ["events", `Exhibitions & honours (${events.length})`],
            ["collections", "Collection carousel"],
            ["site", "Site"],
            ["enquiries", `Enquiries (${enquiries.length})`],
          ].map(([key, labelText]) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              data-testid={`admin-tab-${key}`}
              className={`rounded-full border px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.16em] transition-colors ${
                tab === key ? "border-accent bg-accent text-accent-foreground" : "border-border text-muted-foreground hover:text-foreground"
              }`}
            >
              {labelText}
            </button>
          ))}
        </nav>

        {tab === "artworks" && (
          <div className="space-y-4">
            {editImageArt && (
              <div className="border border-accent/40 bg-card p-6" data-testid="artwork-image-editor-panel">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <h3 className="font-serif text-xl font-medium">Edit image — {editImageArt.title}</h3>
                  <p className="eyebrow text-muted-foreground">The catalogue story stays untouched</p>
                </div>
                {imageBusy ? (
                  <div className="flex items-center gap-3 py-16 text-sm text-muted-foreground">
                    <Loader2 size={16} className="animate-spin" /> Saving the edited image…
                  </div>
                ) : (
                  <ImageEditor src={assetUrl(editImageArt.image)} onUse={onReplaceImage} onCancel={() => setEditImageArt(null)} />
                )}
              </div>
            )}
            {artworks.map((a) => (
              <div key={a.id} className="border border-border bg-card p-5" data-testid={`admin-artwork-row-${a.id}`}>
                <div className="flex flex-wrap items-center gap-5">
                  <img src={assetUrl(a.image)} alt={a.title} className="h-16 w-auto border border-border object-contain" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-serif text-lg font-medium">
                      {a.title} {a.featured && <Bindi size={7} className="ml-1" />}
                    </p>
                    <p className="eyebrow mt-1 text-[10px] text-muted-foreground">
                      {(a.themes || []).join(" · ")} · Order {a.order} {a.ai_generated ? "· AI draft" : "· Reviewed"}
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <button onClick={() => { setEditImageArt(a); window.scrollTo({ top: 0, behavior: "smooth" }); }} data-testid={`admin-edit-image-${a.id}`} className={btn}>
                      <ImagePlus size={13} /> Edit image
                    </button>
                    <button onClick={() => { setAssistFor(assistFor === a.id ? null : a.id); setCarouselFor(null); }} data-testid={`admin-assist-${a.id}`} className={btn}>
                      <Wand2 size={13} /> AI assist
                    </button>
                    <button onClick={() => { setCarouselFor(carouselFor === a.id ? null : a.id); setAssistFor(null); }} data-testid={`admin-carousel-${a.id}`} className={btn}>
                      <Images size={13} /> Carousel
                    </button>
                    <button onClick={() => onRegenerate(a.id)} disabled={busyId === a.id} data-testid={`admin-regenerate-${a.id}`} className={btn}>
                      {busyId === a.id ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
                      Rewrite with AI
                    </button>
                    <button onClick={() => { setEditing(editing === a.id ? null : a.id); setEditSeed(null); }} data-testid={`admin-edit-${a.id}`} className={btn}>
                      {editing === a.id ? <X size={13} /> : <Pencil size={13} />}
                      {editing === a.id ? "Close" : "Edit"}
                    </button>
                    <button onClick={() => onDelete(a.id)} data-testid={`admin-delete-${a.id}`} aria-label="Delete artwork" className="flex h-9 w-9 items-center justify-center rounded-full border border-border transition-colors hover:border-destructive hover:text-destructive">
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>
                {assistFor === a.id && (
                  <AssistPanel
                    art={a}
                    onFilled={(fields) => {
                      setAssistFor(null);
                      setEditSeed(fields);
                      setEditing(a.id);
                    }}
                  />
                )}
                {carouselFor === a.id && <CarouselPanel art={a} existing={a.carousel} />}
                {editing === a.id && (
                  <EditForm
                    art={a}
                    seed={editSeed}
                    onDone={(updated) => {
                      setEditing(null);
                      setEditSeed(null);
                      if (updated) setArtworks((list) => list.map((x) => (x.id === a.id ? updated : x)));
                    }}
                  />
                )}
              </div>
            ))}
            {artworks.length === 0 && (
              <p className="py-16 text-center font-serif text-xl italic text-muted-foreground">No artworks yet — add the first one.</p>
            )}
          </div>
        )}

        {tab === "add" && (
          <div className="max-w-2xl border border-border bg-card p-8">
            <h2 className="font-serif text-2xl font-medium tracking-tight">Add a new artwork</h2>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
              Upload the painting's photograph. You can rotate, straighten, crop and add an optional
              Mithila border first — then Gemini studies the image and Claude writes the museum-style
              title and catalogue note, fully editable before it represents you.
            </p>

            {!file && (
              <>
                <label htmlFor="admin-artwork-upload-input" className="mt-8 flex cursor-pointer flex-col items-center gap-3 border border-dashed border-input px-6 py-12 text-center transition-colors hover:border-accent">
                  <Upload size={22} className="text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">Choose a JPG / PNG / WebP image</span>
                </label>
                <input
                  id="admin-artwork-upload-input"
                  data-testid="admin-artwork-upload-input"
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  className="hidden"
                  onChange={(e) => {
                    setFile(e.target.files?.[0] || null);
                    setPrepared(null);
                    setEditingImage(true);
                  }}
                />
              </>
            )}

            {file && editingImage && !prepared && (
              <ImageEditor
                file={file}
                onUse={(edited) => {
                  setPrepared(edited);
                  setEditingImage(false);
                }}
                onCancel={() => setEditingImage(false)}
              />
            )}

            {file && !editingImage && (
              <div className="mt-6">
                <div className="flex items-center gap-5">
                  <img src={URL.createObjectURL(prepared || file)} alt="Preview" className="max-h-44 w-auto border border-border object-contain" data-testid="artwork-upload-preview" />
                  <div className="space-y-2">
                    <p className="text-sm font-medium">{prepared ? "Edited version ready" : "Original photo will be used"}</p>
                    <button type="button" onClick={() => setEditingImage(true)} data-testid="artwork-reedit-button" className="block font-mono text-[10px] uppercase tracking-[0.16em] text-accent hover:opacity-70">
                      Re-open editor
                    </button>
                    <button type="button" onClick={() => { setFile(null); setPrepared(null); }} className="block font-mono text-[10px] uppercase tracking-[0.16em] text-muted-foreground hover:text-accent">
                      Choose another image
                    </button>
                  </div>
                </div>
                <button onClick={onUpload} disabled={uploading} data-testid="admin-artwork-upload-button" className="mt-6 flex items-center gap-2 rounded-full bg-foreground px-6 py-3 text-[12px] font-semibold text-background transition-colors hover:bg-accent hover:text-accent-foreground disabled:opacity-50">
                  {uploading ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                  {uploading ? "Reading the artwork…" : "Upload & write the story"}
                </button>
              </div>
            )}
          </div>
        )}

        {tab === "events" && (
          <div className="space-y-8">
            <div className="max-w-2xl border border-border bg-card p-8">
              <h2 className="flex items-center gap-3 font-serif text-2xl font-medium tracking-tight">
                <CalendarPlus size={20} className="text-accent" /> Add an exhibition or honour
              </h2>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                Add a picture of the certificate, poster, invitation or event photograph — the studio
                reads it, searches for context, and writes the title, date, venue and description for
                you to review. More pictures from the same event can be attached afterwards; the
                invite is always shown first.
              </p>
              <label htmlFor="admin-event-upload-input" className="mt-6 flex cursor-pointer flex-col items-center gap-3 border border-dashed border-input px-6 py-10 text-center transition-colors hover:border-accent">
                {eventFile ? (
                  <img src={URL.createObjectURL(eventFile)} alt="Event document preview" className="max-h-40 w-auto object-contain" />
                ) : (
                  <Upload size={20} className="text-muted-foreground" />
                )}
                <span className="text-sm text-muted-foreground">
                  {eventFile ? eventFile.name : "Picture of the certificate / poster / invite"}
                </span>
              </label>
              <input id="admin-event-upload-input" data-testid="admin-event-upload-input" type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => setEventFile(e.target.files?.[0] || null)} />
              <div className="mt-5 flex flex-wrap gap-3">
                <button onClick={() => onAddEvent(true)} disabled={!eventFile || eventBusy} data-testid="admin-event-generate-button" className="flex items-center gap-2 rounded-full bg-foreground px-6 py-2.5 text-[12px] font-semibold text-background transition-colors hover:bg-accent hover:text-accent-foreground disabled:opacity-50">
                  {eventBusy ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}
                  {eventBusy ? "Reading the picture…" : "Read details from picture"}
                </button>
                <button onClick={() => onAddEvent(false)} disabled={eventBusy} data-testid="admin-event-manual-button" className={btn}>
                  <Plus size={13} /> Add manually
                </button>
              </div>
            </div>

            <div className="space-y-4">
              {events.map((ev) => (
                <div key={ev.id} className="border border-border bg-card p-5" data-testid={`admin-event-row-${ev.id}`}>
                  <div className="flex flex-wrap items-center gap-5">
                    {ev.image ? (
                      <img src={assetUrl(ev.image)} alt={ev.title} className="h-16 w-auto border border-border object-contain" />
                    ) : (
                      <span className="flex h-16 w-16 items-center justify-center border border-border">
                        <Bindi size={8} className="opacity-40" />
                      </span>
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-serif text-lg font-medium">{ev.title}</p>
                      <p className="eyebrow mt-1 text-[10px] text-muted-foreground">
                        {ev.event_type} · {ev.status === "upcoming" ? "Upcoming" : "Past"}
                        {ev.date_text ? ` · ${ev.date_text}` : ""}
                        {(ev.images || []).length > 0 ? ` · +${ev.images.length} pictures` : ""}
                        {ev.ai_generated ? " · AI draft" : " · Reviewed"}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      {ev.image && (
                        <button onClick={() => onRegenerateEvent(ev.id)} disabled={busyId === ev.id} data-testid={`admin-event-regenerate-${ev.id}`} className={btn}>
                          {busyId === ev.id ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
                          Re-read picture
                        </button>
                      )}
                      <button onClick={() => setEditingEvent(editingEvent === ev.id ? null : ev.id)} data-testid={`admin-event-edit-${ev.id}`} className={btn}>
                        {editingEvent === ev.id ? <X size={13} /> : <Pencil size={13} />}
                        {editingEvent === ev.id ? "Close" : "Edit"}
                      </button>
                      <button onClick={() => onDeleteEvent(ev.id)} data-testid={`admin-event-delete-${ev.id}`} aria-label="Delete event" className="flex h-9 w-9 items-center justify-center rounded-full border border-border transition-colors hover:border-destructive hover:text-destructive">
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </div>
                  {editingEvent === ev.id && (
                    <>
                      <EventForm
                        event={ev}
                        onDone={(updated) => {
                          setEditingEvent(null);
                          if (updated) onEventChanged(updated);
                        }}
                      />
                      <EventImages ev={ev} onChanged={onEventChanged} />
                    </>
                  )}
                </div>
              ))}
              {events.length === 0 && (
                <p className="py-12 text-center font-serif text-xl italic text-muted-foreground">No exhibitions or honours recorded yet.</p>
              )}
            </div>
          </div>
        )}

        {tab === "site" && <SiteTab />}

        {tab === "collections" && <CollectionTab artworks={artworks} />}

        {tab === "enquiries" && (
          <div className="space-y-4">
            {enquiries.map((q) => (
              <div key={q.id} className="border border-border bg-card p-5" data-testid={`admin-enquiry-${q.id}`}>
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <p className="font-serif text-lg font-medium">{q.name}</p>
                  <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                    {q.enquiry_type} · {new Date(q.created_at).toLocaleDateString()} · Email: {q.email_status}
                  </p>
                </div>
                <p className="mt-1 text-sm text-muted-foreground">
                  {q.email} {q.phone ? `· ${q.phone}` : ""}
                </p>
                <p className="mt-3 whitespace-pre-wrap text-sm leading-relaxed">{q.message}</p>
              </div>
            ))}
            {enquiries.length === 0 && (
              <p className="py-16 text-center font-serif text-xl italic text-muted-foreground">No enquiries yet.</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Admin;
